import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import {
  FiEdit2, FiTrash2, FiSearch, FiUser, FiChevronLeft,
  FiChevronRight, FiFileText, FiEye, FiX, FiMail,
  FiPhone, FiMapPin, FiChevronsUp, FiChevronsDown, FiPlus,
} from "react-icons/fi";
import { Country, State, City } from "country-state-city";
import { baseurl } from "../../baseurl";
import axios from "axios";
import Swal from "sweetalert2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FaX } from "react-icons/fa6";
import AddCustomer from "./AddCustomer";

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --ac-teal:      #0a5556;
  --ac-teal-mid:  #0c7172;
  --ac-teal-lite: #14b8a6;
  --ac-gold:      #FFD166;
  --ac-bg:        #eef6f6;
  --ac-white:     #ffffff;
  --ac-border:    rgba(10,85,86,0.10);
  --ac-text:      #0f2a2a;
  --ac-muted:     #5a8080;
  --ac-danger:    #ef4444;
  --ac-success:   #10b981;
  --ac-warning:   #f59e0b;
  --ac-trans:     all 0.18s cubic-bezier(0.4,0,0.2,1);
}

@keyframes ac__fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes ac__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes ac__spin    { to{transform:rotate(360deg)} }
@keyframes ac__modalIn { from{opacity:0;transform:scale(0.93) translateY(16px)} to{opacity:1;transform:scale(1) translateY(0)} }
@keyframes ac__rowIn   { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:translateX(0)} }

/* ── wrapper ── */
.ac__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--ac-bg);
  min-height:100vh;
  animation:ac__fadeUp 0.38s ease both;
}

/* ── header ── */
.ac__hd { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:22px; flex-wrap:wrap; }
.ac__hd-left { display:flex; align-items:center; gap:14px; }
.ac__hd-icon {
  width:52px; height:52px; border-radius:16px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ac-teal),var(--ac-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:22px; color:#fff; box-shadow:0 6px 20px rgba(10,85,86,0.30);
}
.ac__hd-title { font-family:'Sora',sans-serif; font-size:21px; font-weight:800; color:var(--ac-text); letter-spacing:-0.03em; margin:0 0 3px; }
.ac__hd-sub   { font-size:12.5px; color:var(--ac-muted); margin:0; }

/* add customer btn – wrap AddCustomer output */
.ac__add-btn {
  display:inline-flex; align-items:center; gap:7px; padding:10px 18px;
  border-radius:12px; border:none; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  background:linear-gradient(135deg,var(--ac-teal),var(--ac-teal-mid),var(--ac-teal-lite));
  box-shadow:0 4px 14px rgba(10,85,86,0.26); transition:var(--ac-trans);
}
.ac__add-btn:hover { transform:translateY(-1px); box-shadow:0 7px 22px rgba(10,85,86,0.34); }

/* ── main card ── */
.ac__card {
  background:var(--ac-white); border-radius:20px; border:1px solid var(--ac-border);
  box-shadow:0 4px 22px rgba(10,85,86,0.07); overflow:hidden; position:relative;
}
.ac__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--ac-teal),var(--ac-teal-lite),var(--ac-gold));
}

/* ── toolbar ── */
.ac__toolbar {
  display:flex; align-items:center; justify-content:space-between;
  gap:12px; padding:16px 20px; border-bottom:1px solid rgba(10,85,86,0.07);
  flex-wrap:wrap; background:rgba(10,85,86,0.02);
}
.ac__toolbar-left  { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ac__toolbar-right { display:flex; align-items:center; gap:8px; }

.ac__select {
  padding:8px 28px 8px 12px; font-size:12.5px; color:var(--ac-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; outline:none; cursor:pointer; appearance:none;
  font-family:'DM Sans',sans-serif; transition:var(--ac-trans);
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 9px center;
}
.ac__select:focus { border-color:var(--ac-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }

.ac__search {
  display:flex; align-items:center; gap:8px; background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; padding:8px 13px;
  transition:var(--ac-trans); width:270px;
}
.ac__search:focus-within { border-color:var(--ac-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.ac__search svg  { color:var(--ac-muted); flex-shrink:0; }
.ac__search input {
  border:none; outline:none; background:transparent;
  font-size:13px; font-family:'DM Sans',sans-serif; color:var(--ac-text); flex:1;
}
.ac__search input::placeholder { color:#9ababa; }
.ac__clear-x { cursor:pointer; color:var(--ac-muted); transition:color 0.14s; }
.ac__clear-x:hover { color:var(--ac-danger); }
.ac__perpage { display:flex; align-items:center; gap:6px; }
.ac__perpage span { font-size:12px; color:var(--ac-muted); font-family:'Sora',sans-serif; font-weight:600; }

/* ── table ── */
.ac__table-wrap { overflow-x:auto; }
.ac__table { width:100%; border-collapse:collapse; }
.ac__table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--ac-teal);
  background:rgba(10,85,86,0.035); padding:13px 16px;
  white-space:nowrap; border-bottom:1px solid var(--ac-border);
  cursor:pointer; user-select:none; transition:color 0.14s;
}
.ac__table thead th:hover { color:var(--ac-teal-lite); }
.ac__table thead th:first-child { padding-left:22px; }
.ac__table thead th:last-child  { padding-right:22px; text-align:center; }
.ac__sort-icon     { display:inline-flex; vertical-align:middle; margin-left:4px; opacity:0.28; }
.ac__sort-icon--on { opacity:1; color:var(--ac-teal-lite); }

.ac__table tbody tr {
  border-bottom:1px solid rgba(10,85,86,0.05);
  transition:background 0.13s;
  animation:ac__rowIn 0.22s ease both;
}
.ac__table tbody tr:last-child { border-bottom:none; }
.ac__table tbody tr:hover { background:rgba(10,85,86,0.025); }
.ac__table tbody td { padding:13px 16px; font-size:13px; color:var(--ac-text); vertical-align:middle; }
.ac__table tbody td:first-child { padding-left:22px; }
.ac__table tbody td:last-child  { padding-right:22px; text-align:center; }

/* avatar */
.ac__avatar {
  width:36px; height:36px; border-radius:11px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center;
  font-family:'Sora',sans-serif; font-size:14px; font-weight:800; color:#fff;
  background:linear-gradient(135deg,var(--ac-teal),var(--ac-teal-lite));
  box-shadow:0 2px 8px rgba(10,85,86,0.22);
}
.ac__name-cell { display:flex; align-items:center; gap:10px; }
.ac__name-txt  { font-size:13px; font-weight:700; color:var(--ac-text); }

/* contact chips */
.ac__contact { font-size:12.5px; color:var(--ac-muted); display:flex; align-items:center; gap:4px; }
.ac__contact svg { flex-shrink:0; }

/* gstin */
.ac__gstin {
  font-family:'Sora',sans-serif; font-size:11px; font-weight:700;
  color:var(--ac-teal); background:rgba(10,85,86,0.09);
  border:1px solid rgba(10,85,86,0.18); border-radius:7px; padding:3px 9px;
  letter-spacing:0.05em; display:inline-block;
}
.ac__gstin--empty { color:#b0cece; background:transparent; border-color:transparent; }

/* state badge */
.ac__state-badge {
  display:inline-flex; align-items:center; gap:4px; padding:3px 10px;
  border-radius:50px; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
  background:rgba(10,85,86,0.06); color:var(--ac-muted); border:1px solid var(--ac-border);
}

/* view invoices btn */
.ac__view-btn {
  display:inline-flex; align-items:center; gap:5px; padding:7px 13px;
  border-radius:10px; border:1.5px solid rgba(10,85,86,0.20); background:transparent;
  font-size:12px; font-family:'Sora',sans-serif; font-weight:700; color:var(--ac-teal);
  cursor:pointer; transition:var(--ac-trans);
}
.ac__view-btn:hover { background:var(--ac-teal); color:#fff; border-color:var(--ac-teal); box-shadow:0 3px 10px rgba(10,85,86,0.22); }

/* action icon buttons */
.ac__icon-btn {
  width:32px; height:32px; border-radius:9px; border:1.5px solid var(--ac-border);
  display:inline-flex; align-items:center; justify-content:center;
  background:transparent; cursor:pointer; transition:var(--ac-trans);
}
.ac__icon-btn--edit  { color:var(--ac-teal); }
.ac__icon-btn--del   { color:var(--ac-danger); }
.ac__icon-btn--edit:hover { background:var(--ac-teal); color:#fff; border-color:var(--ac-teal); }
.ac__icon-btn--del:hover  { background:var(--ac-danger); color:#fff; border-color:var(--ac-danger); }
.ac__action-cell { display:flex; align-items:center; justify-content:center; gap:6px; }

/* empty */
.ac__empty { padding:60px 20px; text-align:center; }
.ac__empty-icon  { width:60px; height:60px; border-radius:18px; margin:0 auto 14px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:26px; }
.ac__empty-title { font-family:'Sora',sans-serif; font-size:15px; font-weight:700; color:var(--ac-text); margin-bottom:5px; }
.ac__empty-sub   { font-size:13px; color:var(--ac-muted); }

/* skeleton */
.ac__skel {
  border-radius:8px; display:inline-block;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%; animation:ac__shimmer 1.4s linear infinite;
}

/* ── footer / pagination ── */
.ac__footer {
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding:14px 22px; border-top:1px solid rgba(10,85,86,0.07);
  background:rgba(10,85,86,0.02); flex-wrap:wrap;
}
.ac__footer-info { font-size:12px; color:var(--ac-muted); font-family:'Sora',sans-serif; font-weight:600; }
.ac__pages { display:flex; align-items:center; gap:4px; flex-wrap:wrap; }
.ac__pg-btn {
  height:32px; padding:0 10px; border-radius:9px;
  border:1.5px solid var(--ac-border); background:var(--ac-white); cursor:pointer;
  display:inline-flex; align-items:center; gap:4px;
  color:var(--ac-muted); font-size:12px; font-family:'Sora',sans-serif; font-weight:700;
  transition:var(--ac-trans);
}
.ac__pg-btn:hover:not(:disabled) { border-color:var(--ac-teal); color:var(--ac-teal); background:rgba(10,85,86,0.05); }
.ac__pg-btn:disabled { opacity:.35; cursor:not-allowed; }
.ac__pg-num {
  min-width:32px; height:32px; border-radius:9px;
  border:1.5px solid var(--ac-border); background:var(--ac-white); cursor:pointer;
  display:inline-flex; align-items:center; justify-content:center;
  font-size:12.5px; font-family:'Sora',sans-serif; font-weight:700; color:var(--ac-muted);
  padding:0 8px; transition:var(--ac-trans);
}
.ac__pg-num:hover { border-color:var(--ac-teal); color:var(--ac-teal); }
.ac__pg-num--on   { background:var(--ac-teal); color:#fff; border-color:var(--ac-teal); box-shadow:0 3px 8px rgba(10,85,86,0.24); }

/* ══════════════════════════════════════════════
   MODALS (EDIT + INVOICE LEDGER)
══════════════════════════════════════════════ */
.ac__overlay {
  position:fixed; inset:0; z-index:1050;
  background:rgba(10,25,25,0.62); backdrop-filter:blur(7px);
  display:flex; align-items:center; justify-content:center; padding:20px;
  overflow-y:auto;
}

/* ── Edit Modal ── */
.ac__edit-modal {
  background:var(--ac-white); border-radius:24px; width:100%; max-width:660px;
  box-shadow:0 30px 80px rgba(10,85,86,0.22); overflow:hidden; position:relative;
  animation:ac__modalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both;
  margin:auto;
}
.ac__edit-modal::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--ac-teal),var(--ac-teal-lite),var(--ac-gold));
}
.ac__modal-hd {
  display:flex; align-items:center; justify-content:space-between;
  padding:18px 24px 14px; border-bottom:1px solid var(--ac-border);
}
.ac__modal-hd-left { display:flex; align-items:center; gap:12px; }
.ac__modal-hd-icon {
  width:46px; height:46px; border-radius:14px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ac-teal),var(--ac-teal-lite));
  display:flex; align-items:center; justify-content:center; font-size:20px; color:#fff;
  box-shadow:0 4px 14px rgba(10,85,86,0.26);
}
.ac__modal-title { font-family:'Sora',sans-serif; font-weight:800; font-size:15px; color:var(--ac-text); margin:0 0 2px; }
.ac__modal-sub   { font-size:11.5px; color:var(--ac-muted); }
.ac__modal-close {
  width:32px; height:32px; border-radius:9px; border:none; cursor:pointer;
  background:rgba(10,85,86,0.07); color:var(--ac-muted);
  display:flex; align-items:center; justify-content:center; transition:var(--ac-trans);
}
.ac__modal-close:hover { background:rgba(239,68,68,0.10); color:var(--ac-danger); }

/* edit modal form body */
.ac__modal-body { padding:22px 24px; display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.ac__modal-body--full { grid-template-columns:1fr; }
.ac__field-lbl {
  display:block; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
  letter-spacing:0.06em; text-transform:uppercase; color:var(--ac-muted); margin-bottom:7px;
}
.ac__input {
  width:100%; padding:10px 14px; font-size:13.5px; color:var(--ac-text);
  background:#f3fafa; border:1.5px solid rgba(10,85,86,0.14); border-radius:11px;
  outline:none; font-family:'DM Sans',sans-serif; font-weight:500; transition:var(--ac-trans);
}
.ac__input:focus { border-color:var(--ac-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.ac__input--full { grid-column:1/-1; }
.ac__textarea { resize:vertical; min-height:70px; }

/* edit modal footer */
.ac__modal-ft {
  display:flex; gap:10px; padding:16px 24px;
  border-top:1px solid var(--ac-border); background:rgba(10,85,86,0.02);
}
.ac__modal-cancel {
  flex:1; padding:11px; border-radius:12px; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700;
  color:var(--ac-muted); background:rgba(10,85,86,0.06);
  border:1.5px solid rgba(10,85,86,0.14); transition:var(--ac-trans);
}
.ac__modal-cancel:hover { background:rgba(10,85,86,0.12); color:var(--ac-teal); }
.ac__modal-save {
  flex:2; padding:11px; border-radius:12px; border:none; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  background:linear-gradient(135deg,var(--ac-teal),var(--ac-teal-mid),var(--ac-teal-lite));
  box-shadow:0 4px 14px rgba(10,85,86,0.26);
  display:flex; align-items:center; justify-content:center; gap:7px; transition:var(--ac-trans);
}
.ac__modal-save:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 7px 22px rgba(10,85,86,0.34); }
.ac__modal-save:disabled { opacity:.55; cursor:not-allowed; transform:none; }
.ac__spin {
  width:14px; height:14px; border-radius:50%;
  border:2px solid rgba(255,255,255,0.30); border-top-color:#fff;
  animation:ac__spin 0.7s linear infinite;
}

/* ── Invoice Ledger Modal ── */
.ac__ledger-modal {
  background:var(--ac-white); border-radius:24px; width:100%; max-width:860px;
  box-shadow:0 30px 80px rgba(10,85,86,0.22); overflow:hidden; position:relative;
  animation:ac__modalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both;
  margin:auto; max-height:90vh; display:flex; flex-direction:column;
}
.ac__ledger-modal::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--ac-teal),var(--ac-teal-lite),var(--ac-gold));
}
.ac__ledger-body { overflow-y:auto; flex:1; }

/* ledger table */
.ac__ledger-table { width:100%; border-collapse:collapse; }
.ac__ledger-table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--ac-teal);
  background:rgba(10,85,86,0.035); padding:13px 18px;
  white-space:nowrap; border-bottom:1px solid var(--ac-border); position:sticky; top:0;
}
.ac__ledger-table thead th:first-child { padding-left:24px; }
.ac__ledger-table thead th:last-child  { padding-right:24px; text-align:right; }
.ac__ledger-table tbody tr { border-bottom:1px solid rgba(10,85,86,0.05); transition:background 0.13s; }
.ac__ledger-table tbody tr:last-child { border-bottom:none; }
.ac__ledger-table tbody tr:hover { background:rgba(10,85,86,0.025); }
.ac__ledger-table tbody td { padding:13px 18px; font-size:13px; color:var(--ac-text); vertical-align:middle; }
.ac__ledger-table tbody td:first-child { padding-left:24px; }
.ac__ledger-table tbody td:last-child  { padding-right:24px; text-align:right; }

/* invoice number chip */
.ac__inv-num {
  font-family:'Sora',sans-serif; font-size:12px; font-weight:800;
  color:var(--ac-teal); background:rgba(10,85,86,0.09);
  border:1px solid rgba(10,85,86,0.18); border-radius:8px; padding:3px 10px;
  display:inline-block;
}
.ac__inv-date { font-size:12.5px; color:var(--ac-muted); }

/* payment status badges */
.ac__pay-badge {
  display:inline-flex; align-items:center; gap:5px; padding:4px 11px;
  border-radius:50px; font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
}
.ac__pay-badge--paid    { background:rgba(16,185,129,0.10); color:#065f46; border:1px solid rgba(16,185,129,0.25); }
.ac__pay-badge--unpaid  { background:rgba(239,68,68,0.10); color:#991b1b; border:1px solid rgba(239,68,68,0.25); }
.ac__pay-badge--partial { background:rgba(245,158,11,0.12); color:#92400e; border:1px solid rgba(245,158,11,0.30); }
.ac__pay-badge--pending { background:rgba(59,130,246,0.09); color:#1d4ed8; border:1px solid rgba(59,130,246,0.20); }
.ac__pay-dot { width:6px; height:6px; border-radius:50%; display:inline-block; flex-shrink:0; }

/* amount */
.ac__amt-muted { font-size:13px; color:var(--ac-muted); font-weight:500; }
.ac__amt-total { font-family:'Sora',sans-serif; font-size:14px; font-weight:800; color:var(--ac-teal); }

/* ledger footer */
.ac__ledger-ft {
  padding:14px 24px; border-top:1px solid var(--ac-border);
  background:rgba(10,85,86,0.02); display:flex; justify-content:flex-end;
}
.ac__ledger-close {
  padding:10px 22px; border-radius:12px; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700;
  color:var(--ac-muted); background:rgba(10,85,86,0.06);
  border:1.5px solid rgba(10,85,86,0.14); transition:var(--ac-trans);
}
.ac__ledger-close:hover { background:rgba(10,85,86,0.12); color:var(--ac-teal); }

/* ledger empty */
.ac__ledger-empty { padding:60px 20px; text-align:center; }
.ac__ledger-empty-icon { font-size:42px; margin-bottom:14px; opacity:.3; }
.ac__ledger-empty-title { font-family:'Sora',sans-serif; font-size:15px; font-weight:700; color:var(--ac-text); margin-bottom:5px; }
.ac__ledger-empty-sub   { font-size:13px; color:var(--ac-muted); }

@media(max-width:640px){
  .ac__search { width:100%; }
  .ac__toolbar { flex-direction:column; align-items:stretch; }
  .ac__footer  { flex-direction:column; align-items:flex-start; }
  .ac__modal-body { grid-template-columns:1fr; }
}
`;

/* ─── helpers ─── */
const getInitials = (name = "") => name.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";

const getPayBadge = (status = "") => {
  const s = status.toLowerCase();
  if (s === "paid")    return { cls: "ac__pay-badge--paid",    dot: "#10b981" };
  if (s === "unpaid")  return { cls: "ac__pay-badge--unpaid",  dot: "#ef4444" };
  if (s === "partial") return { cls: "ac__pay-badge--partial", dot: "#f59e0b" };
  return                      { cls: "ac__pay-badge--pending", dot: "#3b82f6" };
};

/* ─── Skeleton rows ─── */
const SkeletonRows = ({ count = 5 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i} style={{ borderBottom: "1px solid rgba(10,85,86,0.05)" }}>
      <td style={{ padding: "14px 22px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="ac__skel" style={{ width: 36, height: 36, borderRadius: 11, flexShrink: 0 }} />
          <span className="ac__skel" style={{ width: 110, height: 13 }} />
        </div>
      </td>
      {[130, 100, 100, 70, 80, 70].map((w, j) => (
        <td key={j} style={{ padding: "14px 16px" }}>
          <span className="ac__skel" style={{ width: w, height: 20, borderRadius: 8, display: "block" }} />
        </td>
      ))}
    </tr>
  ));

/* ─── Pagination (UNCHANGED LOGIC) ─── */
const PaginationBar = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;
  let start = Math.max(1, currentPage - 2);
  let end   = Math.min(totalPages, currentPage + 2);
  if (currentPage <= 3)               { start = 1; end = Math.min(5, totalPages); }
  else if (currentPage + 2 >= totalPages) { end = totalPages; start = Math.max(1, totalPages - 4); }
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="ac__pages">
      <button className="ac__pg-btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}>
        <FiChevronLeft size={12} /> Prev
      </button>
      {pages.map(p => (
        <button key={p} className={`ac__pg-num ${currentPage === p ? "ac__pg-num--on" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
      ))}
      <button className="ac__pg-btn" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}>
        Next <FiChevronRight size={12} />
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT — all backend logic UNCHANGED
═══════════════════════════════════════════════ */
function AllCustomer() {
  const token = localStorage.getItem("auth_token");

  /* ── state (ALL UNCHANGED) ── */
  const [customers, setCustomers]               = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [searchTerm, setSearchTerm]             = useState("");
  const [currentPage, setCurrentPage]           = useState(1);
  const [itemsPerPage, setItemsPerPage]         = useState(10);
  const [sortConfig, setSortConfig]             = useState({ key: "name", direction: "asc" });
  const [showModal, setShowModal]               = useState(false);
  const [editCustomer, setEditCustomer]         = useState(null);
  const [saving, setSaving]                     = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading]     = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");

  const Toast = Swal.mixin({
    toast: true, position: "top-end", showConfirmButton: false,
    timer: 2500, timerProgressBar: true,
  });

  /* ── location helpers (ALL UNCHANGED) ── */
  const countries = Country.getAllCountries();
  const getCountryCode = (name) => countries.find(c => c.name === name)?.isoCode || name;
  const currentCountryCode = editCustomer?.country?.length === 2 ? editCustomer.country : getCountryCode(editCustomer?.country);
  const states = currentCountryCode ? State.getStatesOfCountry(currentCountryCode) : [];
  const getStateCode = (name) => states.find(s => s.name === name)?.isoCode || name;
  const currentStateCode = editCustomer?.state?.length === 2 ? editCustomer.state : getStateCode(editCustomer?.state);
  const cities = (currentCountryCode && currentStateCode) ? City.getCitiesOfState(currentCountryCode, currentStateCode) : [];

  /* ── fetchCustomers (UNCHANGED) ── */
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getCustomersByBusiness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setCustomers(res.data.data);
    } catch (err) {
      Swal.fire("Error", "Failed to load customers", "error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchCustomers(); }, []);

  /* ── handleViewInvoices (UNCHANGED) ── */
  const handleViewInvoices = async (customer) => {
    if (!customer.phone) { Toast.fire({ icon: "warning", title: "Customer phone missing" }); return; }
    setInvoiceLoading(true);
    setSelectedCustomerName(customer.name);
    setShowInvoiceModal(true);
    try {
      const res = await axios.get(`${baseurl}getInvoicesByCustomerPhone/${customer.phone}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setCustomerInvoices(res.data.data);
    } catch (err) {
      console.error(err);
      Toast.fire({ icon: "error", title: "Failed to load invoices" });
    } finally {
      setInvoiceLoading(false);
    }
  };

  /* ── sort & search (UNCHANGED) ── */
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const processedCustomers = useMemo(() => {
    let result = [...customers];
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.name?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        String(c.phone || "").includes(s) ||
        c.gstin?.toLowerCase().includes(s)
      );
    }
    result.sort((a, b) => {
      const aVal = a[sortConfig.key] ? String(a[sortConfig.key]).toLowerCase() : "";
      const bVal = b[sortConfig.key] ? String(b[sortConfig.key]).toLowerCase() : "";
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [customers, searchTerm, sortConfig]);

  const totalItems = processedCustomers.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCustomers = processedCustomers.slice(startIndex, startIndex + itemsPerPage);

  /* ── handlers (ALL UNCHANGED) ── */
  const handleEditClick = (customer) => {
    const countryCode = countries.find(c => c.name === customer.country)?.isoCode || customer.country;
    const stateList   = countryCode ? State.getStatesOfCountry(countryCode) : [];
    const stateCode   = stateList.find(s => s.name === customer.state)?.isoCode || customer.state;
    setEditCustomer({ ...customer, country: countryCode, state: stateCode });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") { const d = value.replace(/\D/g, "").slice(0, 10); setEditCustomer({ ...editCustomer, phone: d }); return; }
    if (name === "country") { setEditCustomer({ ...editCustomer, country: value, state: "", city: "" }); return; }
    if (name === "state")   { setEditCustomer({ ...editCustomer, state: value, city: "" }); return; }
    setEditCustomer({ ...editCustomer, [name]: value });
  };

  const handleUpdateCustomer = async () => {
    try {
      setSaving(true);
      const finalCountry = Country.getCountryByCode(editCustomer.country)?.name || editCustomer.country;
      const finalState   = State.getStateByCodeAndCountry(editCustomer.state, editCustomer.country)?.name || editCustomer.state;
      const payload = { ...editCustomer, country: finalCountry, state: finalState };
      await axios.put(`${baseurl}updateCustomer/${editCustomer.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      Toast.fire({ icon: "success", title: "Customer updated" });
      setCustomers(prev => prev.map(c => (c.id === editCustomer.id ? payload : c)));
      setShowModal(false);
    } catch (err) {
      Toast.fire({ icon: "error", title: "Update failed" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?", text: "Delete this customer permanently?", icon: "warning",
      showCancelButton: true, confirmButtonColor: "#ef4444", confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    try {
      await axios.delete(`${baseurl}deleteCustomer/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      Toast.fire({ icon: "success", title: "Deleted" });
      setCustomers(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      Toast.fire({ icon: "error", title: "Delete failed" });
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <span className="ac__sort-icon"><FiChevronsUp size={11} /></span>;
    return sortConfig.direction === "asc"
      ? <span className="ac__sort-icon ac__sort-icon--on"><FiChevronsUp size={11} /></span>
      : <span className="ac__sort-icon ac__sort-icon--on"><FiChevronsDown size={11} /></span>;
  };

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="ac__wrap">

          {/* Header */}
          <div className="ac__hd">
            <div className="ac__hd-left">
              <div className="ac__hd-icon"><FiUser /></div>
              <div>
                <div className="ac__hd-title">All Customers</div>
                <div className="ac__hd-sub">Manage your business contacts</div>
              </div>
            </div>
            <AddCustomer onCustomerAdded={fetchCustomers} />
          </div>

          {/* Main Card */}
          <div className="ac__card">

            {/* Toolbar */}
            <div className="ac__toolbar">
              <div className="ac__toolbar-left">
                <div className="ac__search">
                  <FiSearch size={13} />
                  <input
                    placeholder="Search name, email or phone…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && <FiX size={13} className="ac__clear-x" onClick={() => setSearchTerm("")} />}
                </div>
              </div>
              <div className="ac__toolbar-right">
                <div className="ac__perpage">
                  <span>Show</span>
                  <select
                    className="ac__select"
                    style={{ width: "auto" }}
                    value={itemsPerPage}
                    onChange={e => setItemsPerPage(Number(e.target.value))}
                  >
                    {[5, 10, 20].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="ac__table-wrap">
              <table className="ac__table">
                <thead>
                  <tr>
                    {[
                      { label: "Name",  key: "name" },
                      { label: "Email", key: "email" },
                      { label: "Phone", key: "phone" },
                      { label: "GSTIN", key: "gstin" },
                      { label: "State", key: "state" },
                    ].map(col => (
                      <th key={col.key} onClick={() => requestSort(col.key)}>
                        {col.label} <SortIcon columnKey={col.key} />
                      </th>
                    ))}
                    <th style={{ cursor: "default" }}>Invoices</th>
                    <th style={{ cursor: "default" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SkeletonRows count={itemsPerPage} />
                  ) : paginatedCustomers.length === 0 ? (
                    <tr>
                      <td colSpan="7">
                        <div className="ac__empty">
                          <div className="ac__empty-icon">🔍</div>
                          <div className="ac__empty-title">No customers found</div>
                          <div className="ac__empty-sub">Try adjusting your search term.</div>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedCustomers.map(c => (
                    <tr key={c.id}>
                      <td>
                        <div className="ac__name-cell">
                          <div className="ac__avatar">{getInitials(c.name)}</div>
                          <span className="ac__name-txt">{c.name}</span>
                        </div>
                      </td>
                      <td>
                        {c.email
                          ? <span className="ac__contact"><FiMail size={12} /> {c.email}</span>
                          : <span style={{ color: "#b0cece", fontSize: 12 }}>—</span>
                        }
                      </td>
                      <td>
                        {c.phone
                          ? <span className="ac__contact"><FiPhone size={12} /> {c.phone}</span>
                          : <span style={{ color: "#b0cece", fontSize: 12 }}>—</span>
                        }
                      </td>
                      <td>
                        <span className={`ac__gstin ${!c.gstin ? "ac__gstin--empty" : ""}`}>
                          {c.gstin || "—"}
                        </span>
                      </td>
                      <td>
                        {c.state
                          ? <span className="ac__state-badge"><FiMapPin size={10} /> {c.state}</span>
                          : <span style={{ color: "#b0cece", fontSize: 12 }}>—</span>
                        }
                      </td>
                      <td>
                        <button className="ac__view-btn" onClick={() => handleViewInvoices(c)}>
                          <FiFileText size={12} /> View
                        </button>
                      </td>
                      <td>
                        <div className="ac__action-cell">
                          <button className="ac__icon-btn ac__icon-btn--edit" title="Edit" onClick={() => handleEditClick(c)}>
                            <FiEdit2 size={13} />
                          </button>
                          <button className="ac__icon-btn ac__icon-btn--del" title="Delete" onClick={() => handleDeleteCustomer(c.id)}>
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            {!loading && (
              <div className="ac__footer">
                <div className="ac__footer-info">
                  Showing <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong> to <strong>{Math.min(startIndex + itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> customers
                </div>
                <PaginationBar
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>

        </div>
      </main>

      {/* ══ EDIT MODAL (ALL LOGIC UNCHANGED) ══ */}
      {showModal && editCustomer && (
        <div className="ac__overlay" onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="ac__edit-modal">

            <div className="ac__modal-hd">
              <div className="ac__modal-hd-left">
                <div className="ac__modal-hd-icon"><FiEdit2 /></div>
                <div>
                  <div className="ac__modal-title">Edit Customer</div>
                  <div className="ac__modal-sub">{editCustomer.name}</div>
                </div>
              </div>
              <button className="ac__modal-close" onClick={() => setShowModal(false)}><FiX size={14} /></button>
            </div>

            <div className="ac__modal-body">
              {[
                { key: "name",  label: "Full Name" },
                { key: "email", label: "Email Address" },
                { key: "phone", label: "Phone Number" },
                { key: "gstin", label: "GSTIN" },
              ].map(f => (
                <div key={f.key}>
                  <label className="ac__field-lbl">{f.label}</label>
                  <input
                    className="ac__input"
                    name={f.key}
                    value={editCustomer[f.key] || ""}
                    onChange={handleChange}
                  />
                </div>
              ))}

              {/* Country */}
              <div>
                <label className="ac__field-lbl">Country</label>
                <select className="ac__input" name="country" value={editCustomer.country || ""} onChange={handleChange}>
                  <option value="">Select Country</option>
                  {countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                </select>
              </div>

              {/* State */}
              <div>
                <label className="ac__field-lbl">State</label>
                <select className="ac__input" name="state" value={editCustomer.state || ""} onChange={handleChange} disabled={!editCustomer.country}>
                  <option value="">Select State</option>
                  {states.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                </select>
              </div>

              {/* City */}
              <div>
                <label className="ac__field-lbl">City</label>
                <select className="ac__input" name="city" value={editCustomer.city || ""} onChange={handleChange} disabled={!editCustomer.state}>
                  <option value="">Select City</option>
                  {cities.map(ci => <option key={ci.name} value={ci.name}>{ci.name}</option>)}
                </select>
              </div>

              {/* Zip */}
              <div>
                <label className="ac__field-lbl">Zip Code</label>
                <input className="ac__input" name="zip_code" value={editCustomer.zip_code || ""} onChange={handleChange} />
              </div>

              {/* Address full-width */}
              <div style={{ gridColumn: "1/-1" }}>
                <label className="ac__field-lbl">Address</label>
                <textarea className="ac__input ac__textarea" name="address" value={editCustomer.address || ""} onChange={handleChange} />
              </div>
            </div>

            <div className="ac__modal-ft">
              <button className="ac__modal-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="ac__modal-save" onClick={handleUpdateCustomer} disabled={saving}>
                {saving ? <div className="ac__spin" /> : null}
                {saving ? "Saving…" : "Update Details"}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ══ INVOICE LEDGER MODAL (ALL LOGIC UNCHANGED) ══ */}
      {showInvoiceModal && (
        <div className="ac__overlay" onClick={e => { if (e.target === e.currentTarget) setShowInvoiceModal(false); }}>
          <div className="ac__ledger-modal">

            <div className="ac__modal-hd">
              <div className="ac__modal-hd-left">
                <div className="ac__modal-hd-icon"><FiFileText /></div>
                <div>
                  <div className="ac__modal-title">Customer Ledger</div>
                  <div className="ac__modal-sub">{selectedCustomerName}</div>
                </div>
              </div>
              <button className="ac__modal-close" onClick={() => setShowInvoiceModal(false)}><FiX size={14} /></button>
            </div>

            <div className="ac__ledger-body">
              {invoiceLoading ? (
                <div style={{ padding: "30px 24px" }}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                      <span className="ac__skel" style={{ width: 100, height: 22, borderRadius: 8 }} />
                      <span className="ac__skel" style={{ width: 90, height: 22, borderRadius: 8 }} />
                      <span className="ac__skel" style={{ width: 80, height: 22, borderRadius: 50 }} />
                      <span className="ac__skel" style={{ flex: 1, height: 22, borderRadius: 8 }} />
                    </div>
                  ))}
                </div>
              ) : customerInvoices.length === 0 ? (
                <div className="ac__ledger-empty">
                  <div className="ac__ledger-empty-icon"><FiFileText size={46} /></div>
                  <div className="ac__ledger-empty-title">No invoices found</div>
                  <div className="ac__ledger-empty-sub">This customer has no invoice history yet.</div>
                </div>
              ) : (
                <table className="ac__ledger-table">
                  <thead>
                    <tr>
                      <th>Invoice No</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Subtotal</th>
                      <th style={{ textAlign: "right" }}>Tax</th>
                      <th>Grand Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerInvoices.map(inv => {
                      const pay = getPayBadge(inv.invoice_data?.paymentStatus);
                      return (
                        <tr key={inv.id}>
                          <td>
                            <span className="ac__inv-num">{inv.invoice_data?.invoiceNumber}</span>
                          </td>
                          <td>
                            <span className="ac__inv-date">
                              {new Date(inv.invoice_data?.invoiceDate).toLocaleDateString("en-IN", {
                                day: "2-digit", month: "short", year: "numeric",
                              })}
                            </span>
                          </td>
                          <td>
                            <span className={`ac__pay-badge ${pay.cls}`}>
                              <span className="ac__pay-dot" style={{ background: pay.dot }} />
                              {inv.invoice_data?.paymentStatus}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <span className="ac__amt-muted">₹{Number(inv.invoice_data?.itemSubtotal).toLocaleString()}</span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <span className="ac__amt-muted">₹{Number(inv.invoice_data?.totalTax).toLocaleString()}</span>
                          </td>
                          <td>
                            <span className="ac__amt-total">₹{Number(inv.grand_total).toLocaleString()}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="ac__ledger-ft">
              <button className="ac__ledger-close" onClick={() => setShowInvoiceModal(false)}>Close Ledger</button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

export default AllCustomer;
